import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { AlertTriangle, CheckCircle2, Loader2 } from "@/components/icons"
import { cn } from "@/lib/utils"

type AutoPipelineStatus =
  | "uploading"
  | "indexing"
  | "extracting"
  | "cv_classifying"
  | "cv_matching"
  | "completed"
  | "failed"
  | string

type StepConfig = {
  label: string
  progress: string
  variant: "info" | "success" | "error"
}

const AUTO_DISMISS_MS = 5000

function getStepConfig(status: AutoPipelineStatus, t: (key: string) => string): StepConfig {
  switch (status) {
    case "uploading":
      // Files have already been uploaded directly to blob via SAS by this
      // point; the backend status name is historical. What's happening here
      // is per-document text extraction (markdown / docint).
      return { label: t("tender.autoPipeline.uploading"), progress: "", variant: "info" }
    case "indexing":
      return { label: t("tender.autoPipeline.indexing"), progress: "1/3", variant: "info" }
    case "extracting":
      return { label: t("tender.autoPipeline.extracting"), progress: "2/3", variant: "info" }
    case "cv_classifying":
      return { label: t("tender.autoPipeline.cvClassifying"), progress: "3/3", variant: "info" }
    case "cv_matching":
      return { label: t("tender.autoPipeline.cvMatching"), progress: "3/3", variant: "info" }
    case "completed":
      return { label: t("tender.autoPipeline.completed"), progress: "", variant: "success" }
    case "partial":
      return { label: t("tender.autoPipeline.partial"), progress: "", variant: "error" }
    case "failed":
      return {
        label: t("tender.autoPipeline.failed"),
        progress: "",
        variant: "error",
      }
    default:
      return { label: status, progress: "", variant: "info" }
  }
}

type Props = {
  status: AutoPipelineStatus
  className?: string
}

export function AutoPipelineBanner({ status, className }: Props) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(true)
  const { label, progress, variant } = getStepConfig(status, t)

  useEffect(() => {
    setVisible(true)
    if (status === "completed") {
      const timer = setTimeout(() => setVisible(false), AUTO_DISMISS_MS)
      return () => clearTimeout(timer)
    }
  }, [status])

  if (!visible) return null

  return (
    <div
      role="status"
      className={cn(
        "flex items-center gap-3 border-b px-5 py-2.5 text-sm",
        variant === "info" && "bg-primary/5 border-primary/10 text-primary",
        variant === "success" &&
          "border-success bg-success/10 text-success dark:border-success dark:bg-success/30 dark:text-success",
        variant === "error" &&
          "border-warning bg-warning/10 text-warning-foreground dark:border-warning dark:bg-warning/30 dark:text-warning-foreground",
        className
      )}
    >
      {variant === "info" && <Loader2 className="size-4 shrink-0 animate-spin" />}
      {variant === "success" && <CheckCircle2 className="size-4 shrink-0" />}
      {variant === "error" && <AlertTriangle className="size-4 shrink-0" />}
      <span className="font-medium">{label}</span>
      {progress && (
        <span className="text-primary/60 ml-auto shrink-0 text-xs tabular-nums">{progress}</span>
      )}
    </div>
  )
}
